import type { MeUser } from "../types";
import { getMeApi } from "../api/auth.api";
import { getAuthSessionUser, setAuthSessionUser } from "../api/authSession";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

type AuthContextValue = {
  user: MeUser | null;
  loading: boolean;
  refetch: () => Promise<void>;
  setUser: (u: MeUser | null) => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<MeUser | null>(() => getAuthSessionUser());
  const [loading, setLoading] = useState(true);

  const fetchMe = async () => {
    try {
      const data = await getMeApi();
      const nextUser = data?.user ?? null;
      setAuthSessionUser(nextUser);
      setUser(nextUser);
    } catch (error) {
      const status = (error as any)?.status;
      if (status === 302) return;
      setAuthSessionUser(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, refetch: fetchMe, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
